// React
import { useMemo } from 'react';

// Mobx
import { observer } from 'mobx-react';

// Antd
import { Row, Col, Form, Grid } from 'antd';

// Interfaces
import { ISharedCompanyDataProps } from "./interfaces";

// Components
import {
  BackOfficeBasePhotoUploader,
  FormLabel,
  FormSelectOption,
  FormTextArea,
  FormTextField
} from 'components';

// Custom Hook
import {
  useValidation,
} from 'hooks';

import { FILE_SIZE_0_5_MB } from 'constant/file.constant';

const SharedCompanyData = observer(({
  isCompanyDataEditable, 
  companyData, 
  form, 
  masterCompany_detail, 
  masterCompany_dropdown, 
  masterCompany_loading, 
  masterField_dropdown,
  masterField_isLoading,
  masterBranch_loading,
  className,
  onChangeForm, 
  onFinish,
  renderFooter
  }: ISharedCompanyDataProps) => {

  const { validation_validate } = useValidation();
  const { md, lg, xl } = Grid.useBreakpoint();
  const companyDataFormStyle = useMemo(() => {
    return {
      display: isCompanyDataEditable ? '' : 'none',
      width: md || lg || xl ? '295px' : '100%',
    };
  }, [isCompanyDataEditable, md, lg, xl]);

  return <>
    {/* Form */}
    <Form name="profile-form" form={form} onFinish={onFinish} scrollToFirstError autoComplete="off" requiredMark={false} className={`${className ? className : ''}`}>
      <Row gutter={20} style={{marginLeft: 10}}>
        <Col xs={24} sm={24} md={15} lg={15} xl={15} >

          {/* Job Title */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="app.user.companyData.form.jobTitle" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="jobTitle"
                rules={validation_validate(['required'], 'app.user.companyData.form.jobTitle')}
                style={companyDataFormStyle}
              >
                <FormTextField placeholder="app.user.companyData.formPlaceholder.jobTitle" className="personal-data" />
              </Form.Item>
              {!isCompanyDataEditable && <FormLabel label={companyData.jobTitle || '-'} />}
            </Col>
          </Row>

            {/* Company */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="profileData.companyData.form.company" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="companyId"
                rules={validation_validate(['required'], 'profileData.companyData.form.company')}
                style={companyDataFormStyle}
              >
                <FormSelectOption
                  placeholder="profileData.companyData.formPlaceholder.company"
                  list={masterCompany_dropdown}
                  onChange={(value: string) => onChangeForm && onChangeForm('companyId', value)}
                  loading={masterCompany_loading?.isDropdownLoading}
                  disabled={masterCompany_loading?.isDropdownLoading}
                />
              </Form.Item>
              {!isCompanyDataEditable && <FormLabel label={companyData.companyName || '-'} />}
            </Col>
          </Row>

          {/* Branch */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="profileData.companyData.form.branch" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="branchId"
                rules={validation_validate(['required'], 'profileData.companyData.form.branch')}
                style={companyDataFormStyle}
              >
                <FormSelectOption
                  placeholder="profileData.companyData.formPlaceholder.branch"
                  list={masterCompany_detail?.branch}
                  loading={masterCompany_loading?.isDropdownLoading || masterBranch_loading?.isLoading}
                  disabled={masterCompany_loading?.isDropdownLoading}
                  onChange={(value: string) => onChangeForm && onChangeForm('branchId', value)}
                />
              </Form.Item>
              {!isCompanyDataEditable && <FormLabel label={companyData.branchName || '-'} />}
            </Col>
          </Row>
                                            
          {/* Field */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="app.user.companyData.form.field" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="fieldId"
                rules={validation_validate(['required'], 'app.user.companyData.form.field')}
                style={companyDataFormStyle}
              >
                <FormSelectOption
                  placeholder="app.user.companyData.formPlaceholder.field"
                  list={masterField_dropdown}
                  loading={masterField_isLoading}
                  disabled={masterField_isLoading}
                />
              </Form.Item>
              {!isCompanyDataEditable && <FormLabel label={companyData.fieldName || '-'} />}
            </Col>
          </Row>
          
          {/* Company Email */}
          <Row className="mb-4 mt-6">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="app.user.companyData.form.companyEmail" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="companyEmail"
                rules={validation_validate(['required', 'email'], 'app.user.companyData.form.companyEmail')}
                style={companyDataFormStyle}
              >
                <FormTextField
                  placeholder="app.user.companyData.formPlaceholder.companyEmail"
                  className="personal-data"
                  isEmail
                />
              </Form.Item>
              {!isCompanyDataEditable && <FormLabel label={companyData.companyEmail || '-'} />}
            </Col>
          </Row>

          {/* Company Postal Code */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="app.user.companyData.form.companyPostalCode" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item name="companyPostalCode" style={companyDataFormStyle}>
                <FormTextField
                  placeholder="app.user.companyData.formPlaceholder.companyPostalCode"
                  className="personal-data"
                  readOnly
                  loading={masterBranch_loading?.isDetailLoading}
                />
              </Form.Item>
              {!isCompanyDataEditable && <FormLabel label={companyData.companyPostalCode || '-'} />}
            </Col>
          </Row>

          {/* Company Fax */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="app.user.companyData.form.companyFax" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item name="companyFax" style={companyDataFormStyle}>
                <FormTextField
                  placeholder="app.user.companyData.formPlaceholder.companyFax"
                  className="personal-data"
                  isEmail
                  readOnly
                  loading={masterBranch_loading?.isDetailLoading}
                />
              </Form.Item>
              {!isCompanyDataEditable && <FormLabel label={companyData.companyFax || '-'} />}
            </Col>
          </Row>                                  
                      
          {/* Company Phone */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="app.user.companyData.form.companyPhone" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item name="companyPhone" style={companyDataFormStyle}>
                <FormTextField
                  placeholder="app.user.companyData.formPlaceholder.companyPhone"
                  className="personal-data"
                  readOnly
                  loading={masterBranch_loading?.isDetailLoading}
                />
              </Form.Item>
              {!isCompanyDataEditable && <FormLabel label={companyData.companyPhone || '-'} />}
            </Col>
          </Row>

          {/* Company Address */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="profileData.companyData.form.companyAddress" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item name="companyAddress" style={companyDataFormStyle}>
                <FormTextArea
                  placeholder="profileData.companyData.formPlaceholder.companyAddress"
                  className="personal-data"
                  readOnly
                />
              </Form.Item>
              {!isCompanyDataEditable && <FormLabel label={companyData.companyAddress || '-'} />}
            </Col>
          </Row>
                                                                
          {/* Supervisor Name */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="profileData.companyData.form.supervisorName" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="supervisorName"
                rules={validation_validate(['required', 'alphaNumeric'], 'profileData.companyData.form.supervisorName')}
                style={companyDataFormStyle}
              >
                <FormTextField placeholder="profileData.companyData.formPlaceholder.supervisorName" className="personal-data" />
              </Form.Item>
              {!isCompanyDataEditable && <FormLabel label={companyData.supervisorName || '-'} />}
            </Col>
          </Row>

          {/* Supervisor Email */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="profileData.companyData.form.supervisorEmail" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item
                name="supervisorEmail"
                rules={validation_validate(['required', 'email'], 'profileData.companyData.form.supervisorEmail')}
                style={companyDataFormStyle}
              >
                <FormTextField
                  placeholder="profileData.companyData.formPlaceholder.supervisorEmail"
                  className="personal-data"
                  isEmail
                />
              </Form.Item>
              {!isCompanyDataEditable && <FormLabel label={companyData.supervisorEmail || '-'} />}
            </Col>
          </Row>

          {/* Work Card Photo */}
          <Row className="mb-4">
            <Col xs={24} sm={24} md={8} lg={8} xl={8}>
              <FormLabel label="profileData.companyData.form.workCardPhoto" isBold />
            </Col>
            <Col xs={24} sm={24} md={13} lg={13} xl={13}>
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue, getFieldError }) => {
                  return (
                    <Form.Item
                      noStyle
                      name="workCardPhoto"
                      rules={validation_validate([''], 'profileData.companyData.form.workCardPhoto')}
                    >
                      <BackOfficeBasePhotoUploader
                        image={getFieldValue('workCardPhoto') || companyData.workCardPhoto || ''}
                        onChange={file => onChangeForm && onChangeForm('workCardPhoto', file)}
                        isEditable={isCompanyDataEditable}
                        withoutCard={!isCompanyDataEditable}
                        error={getFieldError('workCardPhoto')?.toString()}
                        maximumFileSize={FILE_SIZE_0_5_MB}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>
      {renderFooter && renderFooter()}
    </Form>
  </>
})

export { SharedCompanyData }